import { normalizedUuidSchema } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useUserDetailQuery } from '@/entities/user';
import { getTrpcErrorCode } from '@/shared/lib';
import { Button, EmptyState, PageHeader } from '@/shared/ui';
import { UserDetail } from '@/widgets/user-detail';

/**
 * 어드민 회원 상세(/users/:userId) — 주소의 회원 id 로 상세를 읽어 위젯에 넘긴다. 머리말은 회원 이름·이메일·목록으로 버튼
 * (받기 전에는 "회원 상세")이고, 불러오는 중·못 찾음·실패는 카드 안 가운데 안내로 보인다.
 */
export const UserDetailPage = (): ReactElement => {
  const navigate = useNavigate();
  const { userId } = useParams<{ userId: string }>();
  const parsed = normalizedUuidSchema.safeParse(userId);
  const id = parsed.success ? parsed.data : '';
  const query = useUserDetailQuery(id, { enabled: parsed.success });
  const user = query.data;

  let body: ReactElement;
  if (!parsed.success) {
    body = <EmptyState isError>올바른 회원 주소가 아닙니다.</EmptyState>;
  } else if (query.isPending) {
    body = <EmptyState>불러오는 중…</EmptyState>;
  } else if (!user && getTrpcErrorCode(query.error) === 'NOT_FOUND') {
    body = <EmptyState isError>회원을 찾을 수 없습니다.</EmptyState>;
  } else if (!user) {
    // 알림(alert)은 오류 글만 읽게 하고, 다시 시도 버튼은 안내 상자 아래 형제로 둔다.
    body = (
      <div className="flex flex-col gap-4">
        <EmptyState isError>회원 정보를 불러오지 못했습니다: {query.error?.message}</EmptyState>
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void query.refetch();
            }}
          >
            다시 시도
          </Button>
        </div>
      </div>
    );
  } else {
    body = <UserDetail key={user.id} user={user} />;
  }

  return (
    <>
      <PageHeader
        title={user ? user.name : '회원 상세'}
        description={user ? user.email : undefined}
        actions={
          <Button
            type="button"
            variant="outline"
            className="h-10 px-5"
            onClick={() => {
              void navigate('/users');
            }}
          >
            <ArrowLeft aria-hidden="true" />
            목록으로
          </Button>
        }
      />
      {body}
    </>
  );
};
